/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { RequiredAsterisk } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { MAX_CHARACTERS_INPUT } from '@atlassian/conversation-assistant-content-renderer/controllers/editor';

import { type PromptCard, roleOptions, useCaseOptions } from '../../../../controllers/prompt-cards';

import { i18n } from './messages';

export type SelectOption = { label: string; value: string };
export type AppSelectOption = SelectOption & { iconUrl?: string };

/**
 * Hook to compute initial data for the create prompt modal.
 * Handles both editing and duplicating flows, prepending "[Copy]" to the title when duplicating.
 */
export function useCreatePromptInitialData({
	editingPromptCard,
	duplicatingPromptCard,
	appOptions,
}: {
	editingPromptCard: PromptCard | null;
	duplicatingPromptCard: PromptCard | null;
	appOptions: Array<{ value: string; label: string }>;
}): CreatePromptFormData | undefined {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		const card = editingPromptCard ?? duplicatingPromptCard;
		if (!card) {
			return undefined;
		}

		const title = duplicatingPromptCard
			? formatMessage(i18n.copyTitlePrefix, { title: card.title })
			: card.title;

		return {
			title,
			prompt: card.prompt,
			useCase: card.useCase ? ({ value: card.useCase, label: card.useCase } as SelectOption) : null,
			role: (card.roles ?? []).map((r) => ({ value: r, label: r })),
			app: (card.appIds ?? []).map((id) => {
				const o = appOptions.find((opt) => opt.value === id);
				return o ? { value: o.value, label: o.label } : { value: id, label: id };
			}),
		};
	}, [editingPromptCard, duplicatingPromptCard, appOptions, formatMessage]);
}

const emptyFormState = {
	titleValue: '',
	prompt: '',
	useCase: null as SelectOption | null,
	role: [] as SelectOption[],
	app: [] as SelectOption[],
};

const styles = cssMap({
	headerFlex: {
		flexDirection: 'row-reverse',
		width: '100%',
	},
	requiredNote: {
		marginBottom: token('space.200'),
	},
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
		marginTop: token('space.150'),
	},
	appOptionIcon: {
		height: '16px',
		width: '16px',
		borderRadius: token('radius.small'),
		flexShrink: 0,
	},
});

export type CreatePromptFormData = {
	title: string;
	prompt: string;
	useCase: SelectOption | null;
	role: SelectOption[];
	app: SelectOption[];
};

export type CreatedPromptData = {
	title: string;
	prompt: string;
	useCase: SelectOption | null;
	role: SelectOption[];
	app: SelectOption[];
	shareId: string;
	ownerName?: string;
};

type CreatePromptModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onCreate: (data: CreatePromptFormData) => CreatedPromptData | Promise<CreatedPromptData>;
	onCreateSuccess?: (promptData: CreatedPromptData) => void;
	appOptions?: AppSelectOption[];
	/** When provided, the form is pre-filled (e.g. when editing or duplicating a prompt). */
	initialData?: CreatePromptFormData | null;
	/** When true, the submit button shows "Save" (edit flow). When false/undefined, shows "Create" (new or duplicate). */
	isEditMode?: boolean;
};

type CreatePromptFormBodyProps = {
	initialData?: CreatePromptFormData | null;
	appOptions: AppSelectOption[];
	/** When true, submit button shows "Save"; otherwise "Create". */
	isEditMode?: boolean;
	onClose: () => void;
	onCreate: (data: CreatePromptFormData) => CreatedPromptData | Promise<CreatedPromptData>;
	onCreateSuccess?: (promptData: CreatedPromptData) => void;
};

function CreatePromptFormBody({
	initialData,
	appOptions,
	isEditMode = false,
	onClose,
	onCreate,
	onCreateSuccess,
}: CreatePromptFormBodyProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const initialFormState = useMemo(
		() =>
			initialData
				? {
						titleValue: initialData.title,
						prompt: initialData.prompt,
						useCase: initialData.useCase,
						role: initialData.role,
						app: initialData.app,
					}
				: emptyFormState,
		[initialData],
	);
	const [titleValue, setTitleValue] = useState(initialFormState.titleValue);
	const [prompt, setPrompt] = useState(initialFormState.prompt);
	const [useCase, setUseCase] = useState<SelectOption | null>(initialFormState.useCase);
	const [role, setRole] = useState<SelectOption[]>(initialFormState.role);
	const [app, setApp] = useState<SelectOption[]>(initialFormState.app);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleCreate = useCallback(async () => {
		const data: CreatePromptFormData = {
			title: titleValue,
			prompt,
			useCase,
			role,
			app,
		};
		setError(null);
		setIsSubmitting(true);
		try {
			const result = await onCreate(data);
			onClose();

			if (onCreateSuccess) {
				onCreateSuccess(result);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : formatMessage(i18n.createError));
		} finally {
			setIsSubmitting(false);
		}
	}, [titleValue, prompt, useCase, role, app, onCreate, onCreateSuccess, onClose, formatMessage]);

	const useCaseSelectOptions = useCaseOptions.map((opt) => ({
		label: opt.label,
		value: opt.id,
	}));
	const roleSelectOptions = roleOptions.map((opt) => ({
		label: opt.label,
		value: opt.id,
	}));

	const canCreate = titleValue.trim() !== '' && prompt.trim() !== '' && !isSubmitting;

	const formatAppOptionLabel = useCallback(
		({ iconUrl, label }: AppSelectOption) => (
			<Inline space="space.100" alignBlock="center">
				{iconUrl && (
					<Box xcss={styles.appOptionIcon}>
						<Image width={16} height={16} src={iconUrl} alt={label} />
					</Box>
				)}
				<Text size="medium">{label}</Text>
			</Inline>
		),
		[],
	);

	return (
		<>
			<ModalBody>
				<Box xcss={styles.requiredNote}>
					<Text size="small" color="color.text.subtle">
						{formatMessage(i18n.requiredFieldsNote)}
						<RequiredAsterisk />
					</Text>
				</Box>
				{error && (
					<Box xcss={styles.requiredNote}>
						<Text size="small" color="color.text.danger">
							{error}
						</Text>
					</Box>
				)}
				<Stack space="space.200">
					<Stack space="space.075">
						<Box as="label" htmlFor="create-prompt-title-field">
							<Text weight="medium" size="small">
								{formatMessage(i18n.titleLabel)}
								<RequiredAsterisk />
							</Text>
						</Box>
						<Textfield
							id="create-prompt-title-field"
							maxLength={255}
							placeholder={formatMessage(i18n.titlePlaceholder)}
							value={titleValue}
							onChange={(e) => setTitleValue((e.target as HTMLInputElement).value)}
						/>
					</Stack>
					<Stack space="space.075">
						<Box as="label" htmlFor="create-prompt-prompt-field">
							<Text weight="medium" size="small">
								{formatMessage(i18n.promptLabel)}
								<RequiredAsterisk />
							</Text>
						</Box>
						<TextArea
							id="create-prompt-prompt-field"
							maxLength={MAX_CHARACTERS_INPUT}
							placeholder={formatMessage(i18n.promptPlaceholder)}
							value={prompt}
							onChange={(e) => setPrompt((e.target as HTMLTextAreaElement).value)}
							minimumRows={4}
						/>
					</Stack>
					<Stack space="space.075">
						<Text weight="medium" size="small">
							{formatMessage(i18n.appLabel)}
						</Text>
						<Select<AppSelectOption, true>
							isMulti
							label={formatMessage(i18n.appLabel)}
							placeholder={formatMessage(i18n.appPlaceholder)}
							options={appOptions}
							value={app}
							onChange={(option) => setApp((option as AppSelectOption[] | null) ?? [])}
							formatOptionLabel={formatAppOptionLabel}
						/>
					</Stack>
					<Stack space="space.075">
						<Text weight="medium" size="small">
							{formatMessage(i18n.useCaseLabel)}
						</Text>
						<Select
							label={formatMessage(i18n.useCaseLabel)}
							placeholder={formatMessage(i18n.useCasePlaceholder)}
							options={useCaseSelectOptions}
							value={useCase}
							onChange={(option) => setUseCase(option as SelectOption | null)}
						/>
					</Stack>
					<Stack space="space.075">
						<Text weight="medium" size="small">
							{formatMessage(i18n.roleLabel)}
						</Text>
						<Select
							isMulti
							label={formatMessage(i18n.roleLabel)}
							placeholder={formatMessage(i18n.rolePlaceholder)}
							options={roleSelectOptions}
							value={role}
							onChange={(option) => setRole((option as SelectOption[] | null) ?? [])}
						/>
					</Stack>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Box xcss={styles.footer}>
					<Button appearance="subtle" onClick={onClose}>
						{formatMessage(i18n.cancelButton)}
					</Button>
					<Button
						appearance="primary"
						onClick={handleCreate}
						isDisabled={!canCreate}
						isLoading={isSubmitting}
					>
						{formatMessage(isEditMode ? i18n.saveButton : i18n.createButton)}
					</Button>
				</Box>
			</ModalFooter>
		</>
	);
}

export const CreatePromptModal = ({
	isOpen,
	onClose,
	onCreate,
	onCreateSuccess,
	appOptions = [],
	initialData,
	isEditMode = false,
}: CreatePromptModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="medium" shouldScrollInViewport>
					<ModalHeader hasCloseButton={false}>
						<Flex gap="space.200" justifyContent="space-between" xcss={styles.headerFlex}>
							<Flex justifyContent="end">
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={formatMessage(i18n.closeButtonLabel)}
									onClick={onClose}
									shape="circle"
								/>
							</Flex>
							<Flex justifyContent="start" alignItems="center">
								<ModalTitle>{formatMessage(i18n.createNewPromptTitle)}</ModalTitle>
							</Flex>
						</Flex>
					</ModalHeader>
					<CreatePromptFormBody
						key={initialData?.title ?? 'create'}
						initialData={initialData}
						appOptions={appOptions ?? []}
						isEditMode={isEditMode}
						onClose={onClose}
						onCreate={onCreate}
						onCreateSuccess={onCreateSuccess}
					/>
				</Modal>
			)}
		</ModalTransition>
	);
};
